import {PhoneHQGlyph} from 'ch3-ui-lib';
import React, { useState, useRef, useEffect } from 'react';
import PlaybackSpeedButton from './PlaybackSpeedButton.tsx';
import {usePlaybackSpeed} from '../../hooks/usePlaybackSpeed.ts';
import {formatMinutes, formatSeconds} from "../../utilis/timeUtilis.ts";
import {playMono} from './monoPlayback';

interface AudioPlayerProps {
    src: string;
    disabled?: boolean;
    filename?: string;
    className?: string;
    waveformData?: number[];
    showSpeedControl?: boolean;
}

const PHQAudioPlayer: React.FC<AudioPlayerProps> = ({ src, className, waveformData, showSpeedControl = true }) => {
    const [hideIcon, setHideIcon] = useState(false);
    const [audioCurrentMinutes, setAudioCurrentMinutes] = useState('00');
    const [audioCurrentSeconds, setAudioCurrentSeconds] = useState('00');
    const [currentProgressValue, setCurrentProgressValue] = useState(0);
    const [barCount, setBarCount] = useState(50);
    const audioRef = useRef<HTMLAudioElement>(null);
    const trackLineRef = useRef<HTMLInputElement>(null);
    const {rate, cycleRate} = usePlaybackSpeed(audioRef, src);
    const waveformRef = useRef<HTMLDivElement>(null);

    const generateMockWaveform = (count: number): number[] => {
        const mockData: number[] = [];
        for (let i = 0; i < count; i++) {
            const baseWave = Math.sin(i * 0.3) * 0.3 + 0.1;
            const noise = Math.random() * 0.2;
            const value = Math.max(0.2, Math.min(1, baseWave + noise));
            mockData.push(value);
        }
        return mockData;
    };

    // Bar layout: 2px bar + 2px gap → 4px slot. Recalc on resize so wider
    // containers don't end up with sparse samples.
    useEffect(() => {
        if (!waveformRef.current || typeof ResizeObserver === 'undefined') return;
        const slotPx = 4;
        const update = (width: number) => {
            setBarCount(Math.max(20, Math.floor(width / slotPx)));
        };
        update(waveformRef.current.getBoundingClientRect().width);
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) update(entry.contentRect.width);
        });
        ro.observe(waveformRef.current);
        return () => ro.disconnect();
    }, []);

    const downsampledWaveform = React.useMemo(() => {
        if (!waveformData || waveformData.length === 0) {
            return generateMockWaveform(barCount);
        }

        if (waveformData.length <= barCount) return waveformData;

        const sampledData: number[] = [];
        const step = waveformData.length / barCount;

        for (let i = 0; i < barCount; i++) {
            const start = Math.floor(i * step);
            const end = Math.floor((i + 1) * step);
            const segment = waveformData.slice(start, end);
            const average = segment.reduce((sum, val) => sum + val, 0) / segment.length;
            sampledData.push(average);
        }

        return sampledData;
    }, [waveformData, barCount]);

    useEffect(() => {
        resetAudio();
    }, [src]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, []);

    const handleMetadataLoaded = () => {
        if (audioRef.current) {
            setAudioCurrentMinutes(formatMinutes(audioRef.current.duration));
            setAudioCurrentSeconds(formatSeconds(audioRef.current.duration));
        }
    };

    const resetAudio = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            setCurrentProgressValue(0);
            updateTimeline();
            pause();
        }
    };

    const play = () => {
        if (audioRef.current) {
            if (audioRef.current.readyState < 4) return;
            void playMono(audioRef.current);
            setHideIcon(true);
        }
    };

    const pause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setHideIcon(false);
        }
    };

    const currentProgress = () => {
        if (audioRef.current && audioRef.current.duration) {
            const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
            return progress;
        }
        return 0;
    }

    const updateTimeline = () => {
        if (audioRef.current) {
            if (trackLineRef.current) {
                trackLineRef.current.max = audioRef.current.duration.toString();
                trackLineRef.current.value = audioRef.current.currentTime.toString();
            }

            setAudioCurrentMinutes(formatMinutes(audioRef.current.duration - audioRef.current.currentTime));
            setAudioCurrentSeconds(formatSeconds(audioRef.current.duration - audioRef.current.currentTime));
            setCurrentProgressValue(currentProgress());
        }
    };

    const updateAudio = () => {
        if (audioRef.current) {
            updateTimeline();
        }
    };

    const endAudio = () => {
        pause();
        resetAudio();
    };

    const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (audioRef.current && trackLineRef.current) {
            const newTime = parseFloat(e.target.value);
            audioRef.current.currentTime = newTime;
            const newProgress = (newTime / audioRef.current.duration) * 100;
            setCurrentProgressValue(newProgress);
            updateTimeline();
        }
    };

    const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (audioRef.current && waveformRef.current) {
            const rect = waveformRef.current.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = clickX / rect.width;
            const newTime = percentage * audioRef.current.duration;
            audioRef.current.currentTime = newTime;
            const newProgress = percentage * 100;
            setCurrentProgressValue(newProgress);
            updateTimeline();
        }
    };

    return (
        src &&
        <div onClick={event => event.stopPropagation()} className={`player-wrapper py-3 bg-transparent ${className}`}>
            <div className="player-content rounded-xl w-full text-zinc-950 flex gap-3 items-center border border-zinc-300 bg-surface p-3">
                <div className="controls rounded-xl bg-indigo-600 min-h-8 min-w-8 flex justify-center items-center">
                    <button type="button" aria-label="Pause" onClick={pause} className={`player-icon flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-white ${!hideIcon ? 'hidden' : ''}`}><PhoneHQGlyph name="pause" size={24}/></button>
                    <button type="button" aria-label="Play" onClick={play} className={`player-icon flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-white ${hideIcon ? 'hidden' : ''}`}><PhoneHQGlyph name="play_arrow" size={24}/></button>
                </div>
                {showSpeedControl && <PlaybackSpeedButton rate={rate} onClick={cycleRate} shape="rounded-square" />}
                <div className="timeline w-full overflow-hidden">
                    {downsampledWaveform && downsampledWaveform.length > 0 ? (
                        <div
                            ref={waveformRef}
                            onClick={handleWaveformClick}
                            className="flex items-center justify-between h-8 cursor-pointer w-full"
                        >
                            {downsampledWaveform.map((level, i) => {
                                const height = Math.max(4, level * 64);
                                const progressPercentage = currentProgressValue;
                                const barStartPercentage = (i / downsampledWaveform.length) * 100;
                                const barEndPercentage = ((i + 1) / downsampledWaveform.length) * 100;
                                const barMidPercentage = (barStartPercentage + barEndPercentage) / 2;
                                const isPlayed = progressPercentage >= barMidPercentage;

                                return (
                                    <div
                                        key={i}
                                        className="rounded-full transition-colors duration-75"
                                        style={{
                                            height: `${height}px`,
                                            backgroundColor: isPlayed ? '#6133EA' : '#DFE0E2',
                                            width: '2px',
                                            flexShrink: 0,
                                        }}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <input
                            ref={trackLineRef}
                            type="range"
                            min="0"
                            max="100"
                            step="0.01"
                            value={audioRef.current?.currentTime || 0}
                            className="timeline-input w-full h-1 bg-brand appearance-none"
                            onChange={handleTimelineChange}
                            style={{
                                background: `linear-gradient(to right, #6133EA ${currentProgressValue}%, #DFE0E2 ${currentProgressValue}%)`
                            }}
                        />
                    )}
                </div>
                <div className="controls-wrapper flex justify-between items-center">
                    <div className="time text-ink min-w-8">
                        <span className='text-xs font-medium'>{audioCurrentMinutes}:{audioCurrentSeconds}</span>
                    </div>
                </div>
                {/*<div>*/}
                {/*    <Download onClick={() => {*/}
                {/*        if (audioRef.current) {*/}

                {/*            const link = document.createElement('a');*/}
                {/*            link.href = audioRef.current.src;*/}
                {/*            link.download = filename + '.mp3' || 'audio-file.mp3';*/}
                {/*            document.body.appendChild(link);*/}
                {/*            link.click();*/}
                {/*            document.body.removeChild(link);*/}
                {/*        }*/}
                {/*    }} strokeWidth={2} size={20} className={'text-brand-ink cursor-pointer'} />*/}
                {/*</div>*/}
            </div>
            <audio
                ref={audioRef}
                src={src}
                onTimeUpdate={updateAudio}
                onEnded={endAudio}
                onPause={() => setHideIcon(false)}
                onLoadedMetadata={handleMetadataLoaded}
                className="hidden"
            />
        </div>
    );
};

export default PHQAudioPlayer;